import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { requestMobile } from '@/lib/request'
import { RefreshCw, User, Smartphone, Wallet, ChevronRight } from 'lucide-react'

interface Account {
  id: string
  remarks_name: string
  phone: string
  plat: string
  total_account: number
}

export default function Account() {
  const [accounts, setAccounts] = useState<Account[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [pageNum, setPageNum] = useState(1)
  const navigate = useNavigate()

  const fetchAccounts = async (page = 1, reset = false) => {
    setLoading(true)
    try {
      const resp = await requestMobile('/common/apis/v1/get_accounts', {
        page_num: page,
        page_size: 20,
        query_filter: {}
      })

      if (resp && !resp.code) {
        const newAccounts = resp.data.list || []
        setAccounts(reset ? newAccounts : [...accounts, ...newAccounts])
        setHasMore((reset ? newAccounts.length : accounts.length + newAccounts.length) < resp.data.total)
        setPageNum(page)
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAccounts(1, true)
  }, [])

  const handleRefresh = () => {
    fetchAccounts(1, true)
  }

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchAccounts(pageNum + 1, false)
    }
  }

  const handleGoToDetail = (account: Account) => {
    navigate('/mobile/account/detail', {
      state: {
        remarks_name: account.remarks_name,
        plat: account.plat,
        phone: account.phone,
        total_account: account.total_account,
        account_id: account.id
      }
    })
  }

  return (
    <div className="min-h-full bg-background">
      {/* 头部 */}
      <div className="bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60 border-b" style={{ paddingTop: 'max(env(safe-area-inset-top), 16px)' }}>
        <div className="flex items-center justify-between p-4">
          <div>
            <h1 className="text-xl font-bold">账号管理</h1>
            <p className="text-sm text-muted-foreground">{accounts.length} 个账号</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* 账号列表 */}
      <div className="p-4 space-y-3">
        {accounts.length > 0 ? (
          <>
            {accounts.map((account) => (
              <Card
                key={account.id}
                className="overflow-hidden border-border/40 hover:shadow-lg hover:shadow-primary/5 transition-all duration-200 hover:border-primary/20 cursor-pointer"
                onClick={() => handleGoToDetail(account)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {/* 头像 */}
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0">
                        <User className="h-6 w-6 text-primary" />
                      </div>

                      {/* 账号信息 */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-base mb-1.5 truncate">
                          {account.remarks_name}
                        </h3>
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                            <Smartphone className="h-3.5 w-3.5" />
                            <span className="truncate">{account.phone || '未绑定'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                              {account.plat}
                            </span>
                            <div className="flex items-center gap-1 text-green-500">
                              <Wallet className="h-3.5 w-3.5" />
                              <span className="font-semibold text-sm">
                                ¥{account.total_account?.toFixed(2) || '0.00'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 箭头 */}
                    <ChevronRight className="h-5 w-5 text-muted-foreground/50 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* 加载更多 */}
            {hasMore && (
              <Button
                variant="outline"
                className="w-full"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    加载中...
                  </>
                ) : (
                  '加载更多'
                )}
              </Button>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <User className="h-12 w-12 mb-4 opacity-20" />
            <p className="text-lg font-medium">{loading ? '加载中...' : '暂无账号'}</p>
            {!loading && <p className="text-sm mt-1">暂无账号信息</p>}
          </div>
        )}
      </div>
    </div>
  )
}
